import { useSelector } from 'react-redux';

import { TABLE_GRID, DEFAULT_ROW_COUNT } from 'modules/experiments/constants';
import {
  EXPERIMENTS_TABLE_COLUMN,
  EXPERIMENTS_TABLE_COLUMNS,
  ExperimentsTableRow,
} from 'modules/experiments/types';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { getExperimentsTableDataState } from 'modules/experiments/ducks/selectors';
import { getExperimentColumnData, serializeExperiments } from 'modules/experiments/utils';
import { useTableFacetContext, useTableSortContext } from 'modules/experiments/contexts';
import { COMPONENT_TYPE, NoData } from 'modules/experiments/components/ui';
import { EmptyStateExperiments, ZeroStateExperiments } from 'ui/illustrations';

import { TableWrapper } from './Styled';
import { HeaderRow } from './HeaderRow';
import { SkeletonRow } from './SkeletonRow';
import { ExperimentRow } from './ExperimentRow';

export const ExperimentsIndexTable = () => {
  // Context
  const { sortBy, setSortBy, sortDirection, setSortDirection } = useTableSortContext();
  const { selectedFilters } = useTableFacetContext();

  // Redux
  const { loading, items } = useSelector(getExperimentsTableDataState);
  const { results = [] } = items || {};

  const experiments = serializeExperiments(results);

  // Skeleton
  const skeletonColumnCount = Object.keys(EXPERIMENTS_TABLE_COLUMNS).length + 1;

  // Columns
  const { t, ct } = useExperimentsTranslations();
  const columns: EXPERIMENTS_TABLE_COLUMN[] = getExperimentColumnData(t);
  const tableContent = () => {
    switch (true) {
      case loading:
        return [...Array(DEFAULT_ROW_COUNT)].map((_, i) => (
          <SkeletonRow key={i} tableGrid={TABLE_GRID} skeletonColumnCount={skeletonColumnCount} />
        ));
      case experiments.length > 0:
        return experiments.map((experiment: ExperimentsTableRow) => (
          <ExperimentRow
            key={`conversion-row-${experiment.id}`}
            experiment={experiment}
            tableGrid={TABLE_GRID}
          />
        ));
      case selectedFilters.length > 0 && experiments.length === 0:
        return (
          <NoData
            testId="no-matching-result-icon"
            componentType={COMPONENT_TYPE.TABLE}
            Icon={EmptyStateExperiments}
            title={ct('emptyState.table.titleVariant1')}
            subTitle={ct('emptyState.table.subTitle')}
          />
        );
      default:
        return (
          <NoData
            testId="no-data-available-icon"
            componentType={COMPONENT_TYPE.TABLE}
            Icon={ZeroStateExperiments}
            title={t('noData.table.title')}
            subTitle={t('noData.table.subTitle')}
            linkText={t('noData.link')}
          />
        );
    }
  };
  return (
    <TableWrapper data-test="experiments-index-table">
      <HeaderRow
        sortedBy={sortBy}
        setSortedBy={setSortBy}
        sortDirection={sortDirection}
        setSortDirection={setSortDirection}
        columns={columns}
      />
      {tableContent()}
    </TableWrapper>
  );
};
